import { Mail, Phone, ExternalLink } from "lucide-react";
import { useAnimateOnScroll } from "@/hooks/useAnimateOnScroll";
import { SpotlightIconPattern } from "./SpotlightIconPattern";

interface SpotlightCTAProps {
  ctaText: string;
  ctaLink?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  primaryColor?: string | null;
  secondaryColor?: string | null;
  featuredImageUrl?: string | null;
}

export function SpotlightCTA({
  ctaText,
  ctaLink,
  contactEmail,
  contactPhone,
  primaryColor,
  secondaryColor,
  featuredImageUrl,
}: SpotlightCTAProps) {
  const { ref: animRef, isVisible } = useAnimateOnScroll(0.15);

  const hasContact = ctaLink || contactEmail || contactPhone;
  if (!hasContact) return null;

  const mainLink = ctaLink || (contactEmail ? `mailto:${contactEmail}` : contactPhone ? `tel:${contactPhone}` : '#');
  const isExternal = ctaLink && !ctaLink.startsWith('mailto:') && !ctaLink.startsWith('tel:');

  const accent = primaryColor ?? "#3B82F6";
  const accent2 = secondaryColor ?? accent;

  return (
    <section className="relative w-full px-6 py-24 sm:py-32" style={{ background: "linear-gradient(180deg, #f8fafc 0%, #ffffff 50%, #f8fafc 100%)" }}>
      <SpotlightIconPattern />
      <div
        ref={animRef}
        className={`relative isolate mx-auto max-w-7xl overflow-hidden bg-gray-900 px-6 pt-16 shadow-2xl sm:rounded-3xl sm:px-16 md:pt-24 lg:flex lg:gap-x-20 lg:px-24 lg:pt-0 transition-all duration-1000 ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
        }`}
      >
        {/* Gradient orb */}
        <svg
          viewBox="0 0 1024 1024"
          aria-hidden="true"
          className="absolute top-1/2 left-1/2 -z-10 size-[64rem] -translate-y-1/2 [mask-image:radial-gradient(closest-side,white,transparent)] sm:left-full sm:-ml-80 lg:left-1/2 lg:ml-0 lg:-translate-x-1/2 lg:translate-y-0"
        >
          <circle r="512" cx="512" cy="512" fill="url(#cta-gradient)" fillOpacity="0.7" />
          <defs>
            <radialGradient id="cta-gradient">
              <stop stopColor={accent} />
              <stop offset="1" stopColor={accent2} />
            </radialGradient>
          </defs>
        </svg>

        {/* Text content */}
        <div className="mx-auto max-w-md text-center lg:mx-0 lg:flex-auto lg:py-32 lg:text-left">
          <h2 className="text-3xl font-semibold tracking-tight text-balance text-white sm:text-4xl">
            Ready to Work Together?
          </h2>

          {(contactEmail || contactPhone) && (
            <div className="mt-6 flex flex-col gap-3 text-base text-gray-300">
              {contactEmail && (
                <a href={`mailto:${contactEmail}`} className="inline-flex items-center gap-2 hover:text-white transition-colors">
                  <Mail className="h-4 w-4" />
                  {contactEmail}
                </a>
              )}
              {contactPhone && (
                <a href={`tel:${contactPhone}`} className="inline-flex items-center gap-2 hover:text-white transition-colors">
                  <Phone className="h-4 w-4" />
                  {contactPhone}
                </a>
              )}
            </div>
          )}

          <div className="mt-10 flex items-center justify-center gap-x-6 lg:justify-start">
            <a
              href={mainLink}
              target={isExternal ? '_blank' : undefined}
              rel="noopener noreferrer"
              className="rounded-md px-4 py-2.5 text-sm font-semibold text-gray-900 shadow-xs hover:bg-gray-100 transition-colors bg-white"
            >
              {ctaText}
            </a>
            {ctaLink && contactEmail && (
              <a
                href={`mailto:${contactEmail}`}
                className="text-sm font-semibold text-white hover:text-gray-100 transition-colors"
              >
                Email us <span aria-hidden="true">&rarr;</span>
              </a>
            )}
          </div>
        </div>

        {/* Featured listing image */}
        {featuredImageUrl && (
          <div className="relative mt-16 h-80 lg:mt-0 lg:h-auto lg:flex-1 lg:self-stretch">
            <img
              src={featuredImageUrl}
              alt="Featured listing"
              className="absolute inset-0 h-full w-full object-cover object-center"
            />
          </div>
        )}
      </div>
    </section>
  );
}
